import { useMemo } from "react"

export interface UsePaginationProps {
  totalCount: number
  pageSize: number
  siblingCount?: number
  currentPage: number
}

export const ELLIPSIS_INDICATOR = "..."

/**
 * Calculates the visible page numbers for pagination including ellipses
 * @param props - Configuration object for pagination calculation
 * @returns Array of page numbers and ellipsis indicators
 */
export const usePagination = ({
  totalCount,
  pageSize,
  siblingCount = 2,
  currentPage,
}: UsePaginationProps) => {
  const visiblePageNumbers = useMemo(() => {
    const totalPagesCount = Math.ceil(totalCount / pageSize)

    // Maximum pages to show: current + siblings on each side + first + last + 2 ellipses
    const maxVisiblePageNumbers = siblingCount * 2 + 5

    // Case 1: Show all pages if total pages is less than max visible
    if (maxVisiblePageNumbers >= totalPagesCount) {
      return Array.from({ length: totalPagesCount }, (_, index) => index + 1)
    }

    // Calculate sibling boundaries around current page
    const leftSiblingBoundary = Math.max(currentPage - siblingCount, 1)
    const rightSiblingBoundary = Math.min(currentPage + siblingCount, totalPagesCount)

    // Determine if ellipses should be shown
    // Skip ellipsis if there's only one page gap between siblings and edges
    const shouldShowLeftEllipsis = leftSiblingBoundary > 2
    const shouldShowRightEllipsis = rightSiblingBoundary < totalPagesCount - 2

    const firstPage = 1
    const lastPage = totalPagesCount

    // Case 2: No left ellipsis, but show right ellipsis
    if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
      const leftSidePageCount = 3 + 2 * siblingCount
      const leftSidePages = Array.from({ length: leftSidePageCount }, (_, index) => index + 1)

      return [...leftSidePages, ELLIPSIS_INDICATOR, lastPage]
    }

    // Case 3: Show left ellipsis, but no right ellipsis
    if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
      const rightSidePageCount = 3 + 2 * siblingCount
      const rightSidePages = Array.from(
        { length: rightSidePageCount },
        (_, index) => totalPagesCount - rightSidePageCount + index + 1
      )

      return [firstPage, ELLIPSIS_INDICATOR, ...rightSidePages]
    }

    // Case 4: Show both left and right ellipses
    if (shouldShowLeftEllipsis && shouldShowRightEllipsis) {
      const middlePageRange = Array.from(
        { length: rightSiblingBoundary - leftSiblingBoundary + 1 },
        (_, index) => leftSiblingBoundary + index
      )

      return [firstPage, ELLIPSIS_INDICATOR, ...middlePageRange, ELLIPSIS_INDICATOR, lastPage]
    }

    return []
  }, [totalCount, pageSize, siblingCount, currentPage])

  return visiblePageNumbers
}
